import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UndefinedAble } from 'data/base';
import './AddVersionPopup.scss';
import {
  LoadingDisplayFlag,
  showError,
  showLoadingAndRun,
} from 'store/popup/actions';
import { App } from 'data/terra/App';
import { asResult } from 'util/result';
import { t } from 'helpers/i18n';
import { firstOrUndefined } from 'util/array';
import { RootState } from 'store/reducer';
import { Environment } from 'data/terra/Environment';
import { AppVersion } from 'data/terra/AppVersion';
import { addAppVersion } from 'v2/store/projectDetail/actions/AddAppVersion';
import AppInputSelector from '../input/AppInputSelector';
import AppInputText from '../input/AppInputText';
import { Button, Modal, Space } from 'antd';
import { selectAppVersion } from 'v2/store/projectDetail/actions/SelectAppVersion';
export interface AddVersionPopupProps {
  app: App;
  versions: AppVersion[];
  handleCloseModal: () => void;
  handleOpenVersionNamingRule: () => void;
}

const AddVersionPopup: React.FunctionComponent<AddVersionPopupProps> = (
  props: AddVersionPopupProps
) => {
  const { handleCloseModal, handleOpenVersionNamingRule } = props;
  const dispatch = useDispatch();
  const { environments } = useSelector((state: RootState) => state.common);
  const [sourceVersion, setSourceVersion] = React.useState<
    UndefinedAble<AppVersion>
  >(firstOrUndefined(props.versions));
  const [versionCode, setVersionCode] = React.useState<UndefinedAble<string>>();
  const [defaultEnv, setDefaultEnv] = React.useState<Environment>(
    environments[0]
  );
  const [addVersionError, setAddVersionError] = React.useState<string>();

  const onVersionChange = (value: string) => {
    if (value === '') {
      setSourceVersion(undefined);
    } else {
      const version = props.versions.find((v: AppVersion) => v.id === value);
      version && setSourceVersion(version);
    }
  };

  const onSaveAppVersion = async () => {
    if (versionCode && versionCode.length > 0) {
      const sourceAppVersion = props.versions.find(
        (v: AppVersion) => v.id === sourceVersion?.id
      );

      const result = asResult<AppVersion>(
        await dispatch(
          showLoadingAndRun(
            addAppVersion(versionCode, sourceAppVersion, defaultEnv),
            LoadingDisplayFlag.ShowAll
          )
        )
      );
      if (result.isSuccess()) {
        handleCloseModal();
        const newVersion = result.get()!;
        dispatch(selectAppVersion(newVersion));
      } else {
        dispatch(showError(t('version_code_create_version_error_occur')));
      }
    } else {
      setAddVersionError(t('form_input_blank_error'));
    }
  };

  const onEnvSelect = (value: string) => {
    const env = environments.find((e: Environment) => e.id === value);
    env && setDefaultEnv(env);
  };

  const onAppVersionChanged = (versionCode?: string) => {
    const regex = new RegExp(
      '^(0|[1-9]\\d*)\\.(0|[1-9]\\d*)\\.(0|[1-9]\\d*)(?:-((?:0|[1-9]\\d*|\\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\\.(?:0|[1-9]\\d*|\\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\\+([0-9a-zA-Z-]+(?:\\.[0-9a-zA-Z-]+)*))?$'
    );
    const versionCodeMaxLength = 32;
    if (versionCode) {
      if (versionCode.length > versionCodeMaxLength) {
        setAddVersionError(
          t('version_code_length_exceeded', { length: versionCodeMaxLength })
        );
        return;
      }

      const version = props.versions.find(
        (v: AppVersion) => v.code === versionCode
      );
      if (version) {
        setAddVersionError(t('version_code_existed_error'));
      } else {
        if (regex.test(versionCode)) {
          setAddVersionError(undefined);
        } else {
          setAddVersionError(t('version_code_incorrect_error'));
        }
      }
    } else {
      setAddVersionError(t('form_input_blank_error'));
    }
    setVersionCode(versionCode);
  };

  const onShowGuideLine = () => {
    handleOpenVersionNamingRule();
  };

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={768}
      modalRender={() => (
        <div className="add-version-config-popup-container">
          <p className="ds-typo-title2 m-0 flex-1 mr-12">
            {t('add_new_platform_version', {
              platform_name: props.app.platform.name,
            })}
          </p>
          <p className="ds-typo-body1 m-0 mt-8">
            <div style={{ marginBottom: 24 }}>
              <span onClick={onShowGuideLine} className="naming-version-rule">
                {t('version_code_naming_rule')}
              </span>
            </div>
          </p>
          <div className="add-version-config-popup-content">
            <AppInputText
              label={t('version_code')}
              value={versionCode}
              placeholder={t('version_information')}
              error={addVersionError}
              required
              allowClear
              onInputChanged={onAppVersionChanged}
            />
            <AppInputSelector
              label={t('active_environment')}
              options={environments.map((e: Environment) => ({
                value: e.id,
                label: e.name,
              }))}
              selectedValue={defaultEnv?.name}
              onChange={onEnvSelect}
            />
            <AppInputSelector
              label={t('copy_config_from_other_version')}
              options={props.versions.map((v: AppVersion) => ({
                value: v.id,
                label: v.code,
              }))}
              selectedValue={sourceVersion?.code.toString()}
              onChange={onVersionChange}
              allowClear={true}
              placeholder={t('version_information')}
            />
            <div style={{ height: '8px' }} />
          </div>
          <div className="d-flex justify-content-end mt-16">
            <Space direction="horizontal" size={16}>
              <Button
                className="alert-modal-action-button"
                type="primary"
                ghost
                onClick={handleCloseModal}
              >
                {t('close')}
              </Button>
              <Button
                className="alert-modal-action-button"
                type="primary"
                onClick={onSaveAppVersion}
              >
                {t('add_new')}
              </Button>
            </Space>
          </div>
        </div>
      )}
    />
  );
};

export default memo(AddVersionPopup);
